import { t } from 'i18next';

import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PieceIcon } from '@/features/pieces/components/piece-icon';
import { getPieceDisplayData } from '@/features/pieces/lib/piece-logos.generated';

export const CellWithCopyTooltip = ({
  displayValue,
  tooltipValue,
  truncated,
}: {
  displayValue: string;
  tooltipValue: string;
  truncated?: boolean;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className={truncated ? 'block truncate max-w-[80px]' : undefined}>
        {displayValue}
      </span>
    </TooltipTrigger>
    <TooltipContent className="flex items-center gap-1.5">
      <span>{tooltipValue}</span>
      <CopyButton
        textToCopy={tooltipValue}
        variant="ghost"
        size="icon"
        className="h-5 w-5"
        withoutTooltip
      />
    </TooltipContent>
  </Tooltip>
);

export const ActiveStatusBadge = ({ isActive }: { isActive: boolean }) => (
  <Badge
    variant="outline"
    className={
      isActive
        ? 'bg-green-100 text-green-700 border-green-200'
        : 'bg-red-100 text-red-700 border-red-200'
    }
  >
    {isActive ? t('Active') : t('Inactive')}
  </Badge>
);

export const PieceIconsCell = ({
  pieceNames,
  maxIcons = 2,
}: {
  pieceNames: string[];
  maxIcons?: number;
}) => {
  if (pieceNames.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  const pieces = pieceNames
    .map((name) => getPieceDisplayData(name))
    .filter(Boolean) as { logoUrl: string; displayName: string }[];
  const visible = pieces.slice(0, maxIcons);
  const extra = pieces.length - visible.length;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex gap-2">
          {visible.map((piece) => (
            <PieceIcon
              key={piece.displayName}
              logoUrl={piece.logoUrl}
              displayName={piece.displayName}
              showTooltip={false}
              circle={true}
              size="md"
              border={true}
            />
          ))}
          {extra > 0 && (
            <div className="flex items-center justify-center bg-accent/35 text-accent-foreground p-2 rounded-full border border-solid dark:bg-accent-foreground/25 dark:text-foreground select-none size-[36px]">
              +{extra}
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {pieces.map((p) => p.displayName).join(', ')}
      </TooltipContent>
    </Tooltip>
  );
};
